<script setup>
import { useAuthStore } from "@/stores";
import { ElPopover } from "element-plus";
import { computed } from "vue";
import WeeklyReportSettings from "../WeeklyReportSettings.vue";
import { useWeeklyReportStore } from "@/stores/useWeeklyReportSettings";

const weeklyReportStore = useWeeklyReportStore();
const authStore = useAuthStore();

const isSuperAdmin = computed(() => authStore.isSuperAdmin);
const isAccountManager = computed(() => authStore.isAccountManager);
const isContentAdmin = computed(() => authStore.isContentAdmin);
const isCSM = computed(() => authStore.isCSM);

const routes = [
  {
    name: "Manage Users",
    to: "/central/manage-users",
    urlType: "central",
  },
  {
    name: "Manage Curriculums",
    to: "/manage/curriculums",
    urlType: "lms",
  },
  {
    name: "Manage Courses",
    to: "/manage/courses",
    urlType: "lms",
  },
  {
    name: "Manage Companies",
    to: "/central/companies",
    urlType: "central",
  },
  {
    name: "Manage Stores",
    to: "/central/stores",
    urlType: "central",
  },
  {
    name: "Manage Playlist",
    to: "/manage/playlist",
    urlType: "lms",
  },
  {
    name: "Manage Archives",
    to: "/manage/archives",
    urlType: "lms",
  },
  {
    name: "Usage Report",
    to: "/usage-report",
    urlType: "lms",
  },
];

const filteredRoutes = computed(() => {
  return routes.filter((route) => {
    const routeName = route.name.toLowerCase();

    // CSM users should ONLY see: Manage Users, Manage Stores, Manage Courses, Manage Playlist
    if (isCSM.value && !isSuperAdmin.value && !isAccountManager.value) {
      return (
        routeName === "manage users" ||
        routeName === "manage stores" ||
        routeName === "manage courses" ||
        routeName === "manage playlist"
      );
    }

    // Content Admin cannot see Manage Users
    if (isContentAdmin.value && routeName === "manage users") {
      return false;
    }

    // Super Admin sees everything
    if (isSuperAdmin.value) {
      return true;
    }

    // Account Manager filtering
    if (isAccountManager.value) {
      return (
        routeName === "manage users" ||
        routeName === "manage courses" ||
        routeName === "manage stores" ||
        routeName === "manage playlist"
      );
    }

    // Content Admin filtering
    if (isContentAdmin.value) {
      const allowedForContentAdmin = [
        "manage curriculums",
        "manage courses",
        "manage archives"
      ];
      return allowedForContentAdmin.includes(routeName);
    }

    // Default: hide everything for other roles
    return false;
  });
});

const handleClickShow = (route) => {
  switch (route.name.toLowerCase()) {
    case "manage weekly report":
      weeklyReportStore.showDialog();
      break;

    default:
      break;
  }
};
</script>
<template>
  <div v-bind="$attrs">
    <el-popover :width="220" popper-style="padding: 5px !important;z-index: 3000 !important">
      <template #reference>
        <a
          class="text-untitled-gray-500 border-transparent hover:border-untitled-gray-300 border-b-2 hover:text-untitled-gray-700 px-1 pt-1 text-sm w-full h-full flex justify-center items-center font-medium">
          Admin Portal
        </a>
      </template>
      <template #default>
        <div class="w-full flex flex-col justify-start items-start">
          <div class="w-full" v-for="(route, index) in filteredRoutes" :key="index">
            <router-link
              class="w-full px-2 py-2 hover:bg-gray-200 text-sm text-untitled-gray-700 flex justify-start items-start"
              :to="route.to" v-if="route.urlType === 'lms'">
              {{ route.name }}
            </router-link>
            <a :href="route.to" v-else-if="route.urlType === 'central'" target="_blank"
              class="w-full px-2 py-2 hover:bg-gray-200 text-sm text-untitled-gray-700 flex justify-start items-start">{{
                route.name }}</a>
            <button v-else
              class="w-full px-2 py-2 hover:bg-gray-200 text-sm text-untitled-gray-700 flex justify-start items-start"
              @click="handleClickShow(route)">
              {{ route.name }}
            </button>
          </div>
        </div>
      </template>
    </el-popover>
    <WeeklyReportSettings />
  </div>
</template>
<style lang="scss" scoped></style>
